import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useEvents } from '../../hooks/useEvents';
import { useAttendeeData } from '../../hooks/useAttendeeData';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import EmptyState from '../../components/attendee/EmptyState';
import LoadingState from '../../components/attendee/LoadingState';
import { formatDate, formatDateLong, getDaysToGo, toGoogleCalendar, downloadICSFile, parseLocalDate } from '../../utils/attendeeUtils';

const AUDIENCE_LABELS = {
  men: "Men's Conference",
  women: "Women's Conference",
  youth: 'Youth Event',
  family: 'Family Event',
  seniors: 'Seniors',
  singles: 'Singles',
  general: 'General Conference',
};

const AttendeeSchedulePage = () => {
  const { events: allEvents, loading: eventsLoading } = useEvents();
  const { registrations, savedEvents, loading: attendeeLoading, refresh } = useAttendeeData();
  const { user } = useAuth();
  const [search, setSearch] = useState('');
  const [timeFilter, setTimeFilter] = useState('upcoming');
  const [scopeFilter, setScopeFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');
  const [sortBy, setSortBy] = useState('dateAsc');
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [mobileView, setMobileView] = useState('list');
  const [activeTab, setActiveTab] = useState('overview');
  const [savingEvent, setSavingEvent] = useState(false);

  const loading = eventsLoading || attendeeLoading;

  const registeredEventIds = useMemo(() => {
    return new Set(registrations.filter(r => r.status !== 'cancelled').map(r => r.events?.id).filter(Boolean));
  }, [registrations]);

  const savedEventIds = useMemo(() => {
    return new Set(savedEvents.map(e => e.id));
  }, [savedEvents]);

  const isEventPast = (event) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return parseLocalDate(event.end || event.start || event.date) < today;
  };

  const counts = useMemo(() => {
    return {
      all: allEvents.length,
      registered: allEvents.filter(e => registeredEventIds.has(e.id)).length,
      saved: allEvents.filter(e => savedEventIds.has(e.id)).length,
      upcoming: allEvents.filter(e => !isEventPast(e)).length,
      past: allEvents.filter(e => isEventPast(e)).length,
    };
  }, [allEvents, registeredEventIds, savedEventIds]);

  const locations = useMemo(() => {
    const set = new Set(allEvents.map(e => e.city).filter(Boolean));
    return [...set].sort();
  }, [allEvents]);

  const filteredEvents = useMemo(() => {
    return allEvents.filter(event => {
      const isPast = isEventPast(event);
      if (timeFilter === 'upcoming' && isPast) return false;
      if (timeFilter === 'past' && !isPast) return false;
      if (scopeFilter === 'registered' && !registeredEventIds.has(event.id)) return false;
      if (scopeFilter === 'saved' && !savedEventIds.has(event.id)) return false;
      if (locationFilter !== 'all' && event.city !== locationFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        const text = `${event.title || event.event || ''} ${event.city || ''} ${event.venue || ''} ${event.org || ''}`.toLowerCase();
        if (!text.includes(q)) return false;
      }
      return true;
    }).sort((a, b) => {
      if (sortBy === 'name') {
        return (a.title || a.event || '').localeCompare(b.title || b.event || '');
      }
      const dateA = parseLocalDate(a.start || a.date);
      const dateB = parseLocalDate(b.start || b.date);
      return sortBy === 'dateDesc' ? dateB - dateA : dateA - dateB;
    });
  }, [allEvents, timeFilter, scopeFilter, locationFilter, sortBy, search, registeredEventIds, savedEventIds]);

  const selectedEvent = filteredEvents.find(e => e.id === selectedEventId) || filteredEvents[0];

  useEffect(() => {
    setActiveTab('overview');
  }, [selectedEvent?.id]);

  const handleSelectEvent = (id) => {
    setSelectedEventId(id);
    setMobileView('detail');
  };

  const handleDownloadICS = () => {
    const eventsToExport = scopeFilter === 'registered'
      ? registrations.filter(r => r.status !== 'cancelled').map(r => r.events).filter(Boolean)
      : filteredEvents;
    downloadICSFile(eventsToExport);
  };

  const toggleSaveEvent = async (eventId) => {
    if (!user || savingEvent) return;
    setSavingEvent(true);
    try {
      if (savedEventIds.has(eventId)) {
        await supabase.from('user_saved_events').delete().eq('user_id', user.id).eq('event_id', eventId);
      } else {
        await supabase.from('user_saved_events').insert({ user_id: user.id, event_id: eventId });
      }
      refresh();
    } finally {
      setSavingEvent(false);
    }
  };

  const getScopeBadge = (event) => {
    if (registeredEventIds.has(event.id)) {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300">
          Registered
        </span>
      );
    }
    if (savedEventIds.has(event.id)) {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300">
          Saved
        </span>
      );
    }
    return null;
  };

  const getAudienceChip = (event) => {
    const label = AUDIENCE_LABELS[event.event_audience] || AUDIENCE_LABELS.general;
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-primary/10 text-primary dark:bg-primary/25 dark:text-purple-300">
        {label}
      </span>
    );
  };

  const getDateOverlay = (event) => {
    const start = parseLocalDate(event.start || event.date);
    const end = event.end ? parseLocalDate(event.end) : null;
    const isMultiDay = end && start.toDateString() !== end.toDateString();
    const sameMonth = isMultiDay && start.getMonth() === end.getMonth();
    const month = start.toLocaleDateString('en-US', { month: 'short' }).toUpperCase();
    const days = isMultiDay
      ? sameMonth
        ? `${start.getDate()}\u2013${end.getDate()}`
        : `${start.getDate()}\u2013${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
      : `${start.getDate()}`;
    return { month, days, year: start.getFullYear() };
  };

  const formatEventDate = (event) => {
    const start = parseLocalDate(event.start || event.date);
    const end = event.end ? parseLocalDate(event.end) : null;
    if (end && start.toDateString() !== end.toDateString()) {
      return `${formatDateLong(event.start || event.date)} \u2013 ${formatDateLong(event.end)}`;
    }
    return formatDateLong(event.start || event.date);
  };

  const filterPill = (active) =>
    `inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
      active
        ? 'bg-primary text-white'
        : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
    }`;

  const countChip = (n, active) =>
    `min-w-[20px] text-center px-1 py-0.5 rounded text-xs font-semibold ${
      active ? 'bg-white/25 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
    }`;

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'location', label: 'Location & Travel' },
    ...(selectedEvent?.notes ? [{ id: 'notes', label: 'Notes' }] : []),
  ];

  const quickFacts = selectedEvent ? [
    {
      label: selectedEvent.event_type === 'virtual' ? 'Virtual Event' : selectedEvent.event_type === 'hybrid' ? 'Hybrid Event' : 'In-Person',
      icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0zM15 11a3 3 0 11-6 0 3 3 0 016 0z" />,
    },
    {
      label: selectedEvent.is_paid_event ? `$${selectedEvent.registration_fee || ''} Admission`.trim() : 'Free Admission',
      icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />,
    },
    ...(selectedEvent.airport ? [{
      label: `Fly into ${selectedEvent.airport}`,
      icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />,
    }] : []),
    ...(selectedEvent.hotels?.length ? [{
      label: 'Hotel Options',
      icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />,
    }] : []),
  ] : [];

  if (loading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Schedule</h1>
        <LoadingState variant="list" count={5} />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      {/* Hero header */}
      <div className="relative overflow-hidden rounded-2xl mb-6 bg-gradient-to-br from-[#16072f] via-primary-dark to-primary">
        <svg className="absolute -right-6 -bottom-8 w-48 h-48 text-white/10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
        <div className="relative px-5 sm:px-7 py-7">
          <p className="text-[11px] font-bold tracking-[0.2em] text-purple-300 uppercase">Your Schedule</p>
          <h1 className="text-2xl sm:text-3xl font-bold text-white mt-1">Upcoming Conferences</h1>
          <p className="text-sm text-purple-200/90 mt-1.5">
            Keep track of your registered events, saved events, and upcoming opportunities.
          </p>
        </div>
      </div>

      {/* Filter pills + export */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <button onClick={() => setScopeFilter('all')} className={filterPill(scopeFilter === 'all')}>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          All Events <span className={countChip(counts.all, scopeFilter === 'all')}>{counts.all}</span>
        </button>
        <button onClick={() => setScopeFilter('registered')} className={filterPill(scopeFilter === 'registered')}>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          My Registered <span className={countChip(counts.registered, scopeFilter === 'registered')}>{counts.registered}</span>
        </button>
        <button onClick={() => setScopeFilter('saved')} className={filterPill(scopeFilter === 'saved')}>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
          </svg>
          My Saved <span className={countChip(counts.saved, scopeFilter === 'saved')}>{counts.saved}</span>
        </button>
        <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1" />
        <button onClick={() => setTimeFilter('upcoming')} className={filterPill(timeFilter === 'upcoming')}>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Upcoming <span className={countChip(counts.upcoming, timeFilter === 'upcoming')}>{counts.upcoming}</span>
        </button>
        <button onClick={() => setTimeFilter('past')} className={filterPill(timeFilter === 'past')}>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Past <span className={countChip(counts.past, timeFilter === 'past')}>{counts.past}</span>
        </button>
        <div className="flex-1" />
        <button
          onClick={handleDownloadICS}
          disabled={filteredEvents.length === 0}
          className="inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          Download Calendar
        </button>
      </div>

      {/* Search + location + sort */}
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] gap-3 mb-6">
        <div className="relative">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by event name, city, venue, or keyword..."
            className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>
        <div className="relative">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0zM15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <select
            value={locationFilter}
            onChange={(e) => setLocationFilter(e.target.value)}
            className="appearance-none w-full sm:w-auto pl-9 pr-8 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-700 dark:text-gray-200 focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="all">All Locations</option>
            {locations.map(loc => <option key={loc} value={loc}>{loc}</option>)}
          </select>
          <svg className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
        <div className="relative">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
          </svg>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="appearance-none w-full sm:w-auto pl-9 pr-8 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-700 dark:text-gray-200 focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="dateAsc">Sort by Date</option>
            <option value="dateDesc">Newest First</option>
            <option value="name">Name A–Z</option>
          </select>
          <svg className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {filteredEvents.length === 0 ? (
        <EmptyState
          icon="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
          title={scopeFilter === 'registered' ? 'No registered events' : scopeFilter === 'saved' ? 'No saved events' : 'No events found'}
          message={scopeFilter === 'registered' ? 'You haven\'t registered for any events yet.' : scopeFilter === 'saved' ? 'You haven\'t saved any events yet.' : 'Try adjusting your filters or search.'}
          ctaLabel={scopeFilter !== 'all' ? 'View All Events' : null}
          ctaLink="/events"
        />
      ) : (
        <>
          {/* Mobile back button */}
          {mobileView === 'detail' && (
            <button
              onClick={() => setMobileView('list')}
              className="lg:hidden mb-4 inline-flex items-center gap-2 text-sm text-primary hover:text-primary-dark font-medium"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to list
            </button>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* Event cards */}
            <div className={`lg:col-span-2 space-y-3 ${mobileView === 'detail' ? 'hidden lg:block' : ''}`}>
              <p className="text-xs font-bold tracking-wider uppercase text-gray-500 dark:text-gray-400 px-1">
                {timeFilter === 'past' ? 'Past Events' : 'Upcoming Events'} ({filteredEvents.length})
              </p>
              <div className="space-y-3 max-h-[calc(100vh-340px)] overflow-y-auto pr-1">
                {filteredEvents.map((event) => {
                  const isSelected = selectedEvent?.id === event.id;
                  const ov = getDateOverlay(event);
                  return (
                    <button
                      key={event.id}
                      onClick={() => handleSelectEvent(event.id)}
                      className={`w-full text-left bg-white dark:bg-gray-800 rounded-2xl border p-3 flex items-center gap-3 transition-all ${
                        isSelected
                          ? 'border-primary ring-2 ring-primary/20 shadow-md'
                          : 'border-gray-200 dark:border-gray-700 hover:border-primary/50 hover:shadow-sm'
                      }`}
                    >
                      {/* Image thumb with date overlay */}
                      <div className="relative w-20 h-20 rounded-xl overflow-hidden shrink-0 bg-gradient-to-br from-primary-dark to-primary">
                        {event.image_url && (
                          <img src={event.image_url} alt="" className="absolute inset-0 w-full h-full object-cover" />
                        )}
                        <div className="absolute bottom-0 inset-x-0 bg-black/60 backdrop-blur-[2px] text-center py-0.5">
                          <span className="text-[9px] font-bold tracking-wide text-white leading-none">
                            {ov.month} {ov.days}
                          </span>
                          <div className="text-[8px] text-white/70 leading-none">{ov.year}</div>
                        </div>
                      </div>

                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white leading-snug line-clamp-2">
                          {event.title || event.event}
                        </h4>
                        <p className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">
                          <svg className="w-3 h-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0zM15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          {event.venue ? `${event.venue}, ` : ''}{event.city}
                        </p>
                        <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                          {getAudienceChip(event)}
                          {getScopeBadge(event)}
                        </div>
                      </div>

                      <svg className={`w-5 h-5 shrink-0 ${isSelected ? 'text-primary' : 'text-gray-300 dark:text-gray-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  );
                })}
              </div>

              {/* Tip card */}
              <div className="mt-4 bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-4 flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">Stay prepared!</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 leading-relaxed">
                    Add events to your calendar, get reminders, and never miss an event.
                  </p>
                </div>
              </div>
            </div>

            {/* Event detail */}
            <div className={`lg:col-span-3 ${mobileView === 'list' ? 'hidden lg:block' : ''}`}>
              {selectedEvent ? (
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
                  {/* Banner */}
                  <div className="relative h-52 sm:h-60 bg-gradient-to-r from-primary-dark to-primary">
                    {selectedEvent.image_url && (
                      <img src={selectedEvent.image_url} alt={selectedEvent.title} className="absolute inset-0 w-full h-full object-cover" />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/35 to-black/10" />
                    <div className="absolute top-4 left-4 flex items-center gap-2">
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold tracking-widest uppercase bg-primary/90 text-white">
                        {isEventPast(selectedEvent) ? 'Past Event' : 'Upcoming'}
                      </span>
                      {getScopeBadge(selectedEvent)}
                    </div>
                    <div className="relative h-full flex flex-col justify-end p-5 text-white">
                      <h2 className="text-xl sm:text-2xl font-bold leading-snug">{selectedEvent.title || selectedEvent.event}</h2>
                      <p className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-white/85 mt-2">
                        <span className="inline-flex items-center gap-1.5">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          {formatEventDate(selectedEvent)}
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0zM15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          {selectedEvent.address || [selectedEvent.venue, selectedEvent.city].filter(Boolean).join(', ')}
                        </span>
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-col xl:flex-row">
                    {/* Tabs + content */}
                    <div className="flex-1 min-w-0 p-5">
                      <div className="flex gap-1 border-b border-gray-100 dark:border-gray-700 mb-5">
                        {tabs.map(tab => (
                          <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors border-b-2 -mb-px ${
                              activeTab === tab.id
                                ? 'border-primary text-primary'
                                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                            }`}
                          >
                            {tab.label}
                          </button>
                        ))}
                      </div>

                      {activeTab === 'overview' && (
                        <div>
                          <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">About This Event</h4>
                          <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-5">
                            {selectedEvent.description || 'Details coming soon.'}
                          </p>

                          {/* Quick facts */}
                          {quickFacts.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-6">
                              {quickFacts.map((f, i) => (
                                <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-50 dark:bg-gray-700/50 text-xs font-medium text-gray-700 dark:text-gray-300">
                                  <svg className="w-3.5 h-3.5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">{f.icon}</svg>
                                  {f.label}
                                </span>
                              ))}
                            </div>
                          )}

                          <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Event Details</h4>
                          <div className="space-y-2.5">
                            <div className="flex items-start gap-3 text-sm">
                              <svg className="w-4 h-4 mt-0.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                              <div>
                                <span className="text-gray-500 dark:text-gray-400 text-xs block">Date</span>
                                <span className="text-gray-900 dark:text-white">{formatEventDate(selectedEvent)}</span>
                              </div>
                            </div>
                            <div className="flex items-start gap-3 text-sm">
                              <svg className="w-4 h-4 mt-0.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0zM15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                              </svg>
                              <div>
                                <span className="text-gray-500 dark:text-gray-400 text-xs block">Venue</span>
                                <span className="text-gray-900 dark:text-white">{selectedEvent.venue || selectedEvent.venue_name || 'TBD'}</span>
                                {selectedEvent.city && <span className="text-gray-500 dark:text-gray-400 block text-xs mt-0.5">{selectedEvent.address || selectedEvent.city}</span>}
                              </div>
                            </div>
                            {selectedEvent.website_url && (
                              <div className="flex items-start gap-3 text-sm">
                                <svg className="w-4 h-4 mt-0.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                                </svg>
                                <div>
                                  <span className="text-gray-500 dark:text-gray-400 text-xs block">Website</span>
                                  <a href={selectedEvent.website_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                                    View Event Website
                                  </a>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {activeTab === 'location' && (
                        <div>
                          <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Location & Travel</h4>
                          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 mb-4">
                            <p className="text-sm font-medium text-gray-900 dark:text-white">{selectedEvent.venue || selectedEvent.venue_name || 'Venue TBD'}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{selectedEvent.address || selectedEvent.location || selectedEvent.city}</p>
                          </div>
                          <div className="flex flex-wrap gap-2 mb-4">
                            <a
                              href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(selectedEvent.address || [selectedEvent.venue, selectedEvent.city].filter(Boolean).join(', '))}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                              </svg>
                              Get Directions
                            </a>
                            {selectedEvent.airport && (
                              <span className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                                </svg>
                                Nearest airport: {selectedEvent.airport}
                              </span>
                            )}
                          </div>
                          {selectedEvent.hotels?.length > 0 && (
                            <div>
                              <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Nearby Hotels</h4>
                              <div className="space-y-2">
                                {selectedEvent.hotels.map((h, i) => (
                                  <div key={i} className="flex items-start justify-between gap-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg px-3 py-2.5">
                                    <div className="min-w-0">
                                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{h.name}</p>
                                      {h.note && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{h.note}</p>}
                                    </div>
                                    {h.distance && <span className="text-xs text-primary font-medium shrink-0">{h.distance}</span>}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {activeTab === 'notes' && selectedEvent.notes && (
                        <div>
                          <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Schedule & Notes</h4>
                          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
                            <pre className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap font-sans leading-relaxed">
                              {selectedEvent.notes}
                            </pre>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Action rail */}
                    <div className="w-full xl:w-72 shrink-0 p-5 border-t xl:border-t-0 xl:border-l border-gray-100 dark:border-gray-700 space-y-3">
                      {registeredEventIds.has(selectedEvent.id) ? (
                        <Link
                          to={`/my-events/${selectedEvent.id}`}
                          className="flex items-center justify-center gap-2 w-full px-4 py-2.5 text-sm font-semibold rounded-xl text-white bg-primary hover:bg-primary-dark transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                          </svg>
                          Manage Registration
                        </Link>
                      ) : (
                        <Link
                          to={`/events/${selectedEvent.id}`}
                          className="flex items-center justify-center gap-2 w-full px-4 py-2.5 text-sm font-semibold rounded-xl text-white bg-primary hover:bg-primary-dark transition-colors"
                        >
                          View Details & Register
                        </Link>
                      )}
                      <button
                        onClick={() => toggleSaveEvent(selectedEvent.id)}
                        disabled={savingEvent}
                        className="flex items-center justify-center gap-2 w-full px-4 py-2.5 text-sm font-medium rounded-xl border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors disabled:opacity-60"
                      >
                        <svg className="w-4 h-4" fill={savedEventIds.has(selectedEvent.id) ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                        </svg>
                        {savedEventIds.has(selectedEvent.id) ? 'Saved' : 'Save Event'}
                      </button>
                      <a
                        href={toGoogleCalendar(selectedEvent)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 w-full px-4 py-2.5 text-sm font-medium rounded-xl border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        Add to Calendar
                      </a>

                      {/* Tags */}
                      <div className="pt-3 border-t border-gray-100 dark:border-gray-700">
                        <p className="text-xs font-bold tracking-wider uppercase text-gray-400 dark:text-gray-500 mb-2">Event Tags</p>
                        <div className="flex flex-wrap gap-1.5">
                          {getAudienceChip(selectedEvent)}
                          {selectedEvent.org && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
                              {selectedEvent.org}
                            </span>
                          )}
                          {selectedEvent.event_category && selectedEvent.event_category !== 'conference' && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 capitalize">
                              {selectedEvent.event_category}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Quick info */}
                      <div className="pt-3 border-t border-gray-100 dark:border-gray-700">
                        <p className="text-xs font-bold tracking-wider uppercase text-gray-400 dark:text-gray-500 mb-2">Quick Info</p>
                        <div className="space-y-2 text-sm">
                          {selectedEvent.org && (
                            <div className="flex items-start gap-2.5">
                              <svg className="w-4 h-4 mt-0.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1" />
                              </svg>
                              <div>
                                <span className="text-xs text-gray-500 dark:text-gray-400 block">Organization</span>
                                <span className="text-gray-900 dark:text-white">{selectedEvent.org}</span>
                              </div>
                            </div>
                          )}
                          {!isEventPast(selectedEvent) && (
                            <div className="flex items-start gap-2.5">
                              <svg className="w-4 h-4 mt-0.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <div>
                                <span className="text-xs text-gray-500 dark:text-gray-400 block">Time Until</span>
                                <span className="text-primary font-medium">{getDaysToGo(selectedEvent.start || selectedEvent.date)}</span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-12 text-center">
                  <svg className="mx-auto h-12 w-12 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Select an event to view details</p>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AttendeeSchedulePage;
